// resources/js/src/services/story.api.ts

import { ReactionType } from "../components/community/components/post/interfaces/types";
import { CreateStoryPayload, Story, StoryGroup, StoryReply } from "../components/community/components/stories/interfaces/storyTypes";

const BASE = "/api/v3/stories";

type Envelope<T> = {
  success: boolean;
  message: string;
  data: T;
};

function isEnvelope<T>(body: unknown): body is Envelope<T> {
  return (
    !!body &&
    typeof body === "object" &&
    "success" in body &&
    "data" in body
  );
}

async function request<T>(
  url: string,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
      ...init.headers,
    },
    ...init,
  });

  if (res.status === 204) {
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    return undefined as T;
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* empty body */
  }

  if (!res.ok) {
    const msg =
      (body as { message?: string } | null)?.message ??
      `Request failed (${res.status})`;
    throw new Error(msg);
  }

  if (isEnvelope<T>(body)) return body.data;

  return body as T;
}

export const StoryApi = {
  /** GET /api/v3/stories — feed مجمّع حسب المستخدم */
  list: (): Promise<StoryGroup[]> => request<StoryGroup[]>(BASE),

  /** GET /api/v3/stories/mine — ستوريز المستخدم الحالي (مجموعة واحدة) */
  mine: (): Promise<StoryGroup[]> => request<StoryGroup[]>(`${BASE}/mine`),

  get: (id: number): Promise<Story> => request<Story>(`${BASE}/${id}`),

  create: (payload: CreateStoryPayload): Promise<Story> => {
    const form = new FormData();
    form.append("media", payload.file);
    if (payload.caption) form.append("caption", payload.caption);
    return request<Story>(BASE, { method: "POST", body: form });
  },

  remove: (id: number): Promise<void> =>
    request<void>(`${BASE}/${id}`, { method: "DELETE" }),

  markSeen: (id: number): Promise<void> =>
    request<void>(`${BASE}/${id}/seen`, { method: "POST" }),

  react: (id: number, reaction: ReactionType): Promise<Story> =>
    request<Story>(`${BASE}/${id}/react`, {
      method: "POST",
      body: JSON.stringify({ reaction }),
    }),

  reply: (id: number, text: string): Promise<StoryReply> =>
    request<StoryReply>(`${BASE}/${id}/reply`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
};